import React, { useState, useMemo, useEffect } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { 
  Search01Icon,
  Download01Icon,
  File02Icon,
  DatabaseIcon,
  FilterIcon,
  Calendar01Icon
} from '@hugeicons/core-free-icons';
import CommandPalette from '../../components/CommandPalette';
import Button from '../../components/Button';
import ResponsiveModal from '../../components/ResponsiveModal';
import DateRangeModal from '../../components/DateRangeModal';
import { exportToCSV, exportToPDF } from '../../utils/export';
import { api } from '../../utils/api';
import { FLAGS } from '../../config/flags';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FadeLoader } from 'react-spinners';

const AgentHistory = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isCmdkOpen, setIsCmdkOpen] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [dateFilter, setDateFilter] = useState('30_days');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });

  useEffect(() => {
    const fetchHistory = async (isBackground = false) => {
      if (!isBackground) setLoading(true);
      if (FLAGS.USE_MOCK_API) {
        setHistory([
          { id: 'TXN-1', link_token: 'A1B2C3D4', transaction_type: 'deposit', client: { name: 'Charlie' }, amount_sent: "800.00", created_at: '2026-01-11T10:30:00Z', status: 'completed' },
          { id: 'TXN-2', link_token: 'E5F6G7H8', transaction_type: 'withdrawal', client: { name: 'Alice' }, amount_sent: "120.00", created_at: '2026-01-10T15:45:00Z', status: 'completed' },
        ]);
        setLoading(false);
        return;
      }

      try {
        const response = await api.get(`/agent/transactions?page=${page}&per_page=15`);
        const data = response.data?.data || response.data || {};
        
        const txList = data.transactions || data.data || [];
        
        const normalized = txList.map(tx => {
          const rate = parseFloat(tx.order?.current_rate || 1);
          const amountNgn = parseFloat(tx.amount_sent || 0);
          
          console.log(`AGENT DEBUG TX ${tx.reference}:`, {
            order_type: tx.order?.transaction_type,
            cashback_rate: tx.order?.cashback_rate,
            spread: tx.order?.spread
          });

          // Calculate cashback if applicable
          let cashbackEarned = 0;
          if (tx.order?.transaction_type === 'deposit' && parseFloat(tx.order?.cashback_rate || 0) > 0) {
            const amountUsd = amountNgn / rate;
            cashbackEarned = amountUsd * parseFloat(tx.order.spread || 0) * parseFloat(tx.order.cashback_rate);
          }

          return {
            id: tx.uuid || tx.uid,
            reference: tx.reference,
            link_token: tx.reference, 
            transaction_type: tx.type === 'payment_confirmation' ? (tx.order?.transaction_type || 'deposit') : tx.type,
            client: tx.client,
            merchant: tx.client,
            amount_sent: amountNgn,
            amount_usd: amountNgn / rate,
            currency: tx.currency || 'NGN',
            created_at: tx.created_at,
            status: tx.status,
            order_token: tx.order?.link_token,
            rate: rate,
            cashback_earned: cashbackEarned
          };
        });

        const meta = data.pagination || data.meta || { current_page: 1, last_page: 1, total: normalized.length };
        
        setHistory(normalized);
        setPagination(meta);
      } catch (err) {
        console.error('Failed to fetch agent history:', err);
        if (!isBackground) setHistory([]);
      } finally {
        if (!isBackground) setLoading(false);
      }
    };

    fetchHistory();
    const interval = setInterval(() => fetchHistory(true), 15000); // Poll every 15s
    return () => clearInterval(interval);
  }, [page]);

  const handleDateFilterChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setIsDateModalOpen(true);
    } else {
      setDateFilter(value);
      setCustomStartDate('');
      setCustomEndDate('');
    }
  };

  const handleCustomDateApply = (start, end) => {
    setCustomStartDate(start);
    setCustomEndDate(end);
    setDateFilter('custom');
  };

  const filteredHistory = useMemo(() => {
    let data = history;

    if (filterType !== 'all') {
      data = data.filter(tx => tx.transaction_type.toLowerCase() === filterType.toLowerCase());
    }

    const now = new Date();
    
    if (dateFilter === 'custom' && customStartDate && customEndDate) {
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      data = data.filter(tx => {
        const txDate = new Date(tx.created_at);
        return txDate >= start && txDate <= end;
      });
    } else if (dateFilter !== 'custom') {
      const days = dateFilter === '30_days' ? 30 : dateFilter === '3_months' ? 90 : 365;
      const cutoff = new Date(now);
      cutoff.setDate(cutoff.getDate() - days);
      data = data.filter(tx => new Date(tx.created_at) >= cutoff);
    }

    return data;
  }, [history, filterType, dateFilter, customStartDate, customEndDate]);

  const handleExportPDF = () => {
    const headers = ["Date & Time", "Order ID", "Type", "Client", "USD", "NGN", "Status"];
    const mapper = (tx) => [
      new Date(tx.created_at).toLocaleString(), 
      tx.order_token || tx.reference || tx.id, 
      tx.transaction_type, 
      tx.client?.name || tx.client?.email || 'N/A', 
      `$${parseFloat(tx.amount_usd || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 
      `₦${parseFloat(tx.amount_sent || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 
      tx.status
    ];
    const userName = user?.business_name || user?.email || 'Authorized Agent';
    exportToPDF(filteredHistory, 'Agent Transaction History', headers, mapper, userName);
    setIsExportModalOpen(false);
  };

  const handleExportCSV = () => {
    const flatData = filteredHistory.map(tx => ({
      date: new Date(tx.created_at).toLocaleString(),
      order_id: tx.order_token || tx.reference || tx.id,
      type: tx.transaction_type,
      client: tx.client?.name || tx.client?.email || 'N/A',
      amount_usd: parseFloat(tx.amount_usd || 0).toFixed(2),
      amount_ngn: parseFloat(tx.amount_sent || 0).toFixed(2),
      status: tx.status
    }));
    exportToCSV(flatData);
    setIsExportModalOpen(false);
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <FadeLoader color="#4ade80" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20">
      <CommandPalette 
        isOpen={isCmdkOpen} 
        setIsOpen={setIsCmdkOpen} 
        transactions={filteredHistory} 
      />
      <DateRangeModal
        isOpen={isDateModalOpen}
        onClose={() => setIsDateModalOpen(false)}
        onApply={handleCustomDateApply}
      />

      <ResponsiveModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        title="Filter History"
      >
        <div className="py-6 space-y-6">
          <div className="space-y-2">
            <label className="text-xs font-black text-muted-gray uppercase tracking-widest">Date Range</label>
            <select 
              value={dateFilter}
              onChange={handleDateFilterChange}
              className="w-full border border-light-gray/20 dark:border-gray-700/50 rounded-2xl px-4 py-3.5 text-sm bg-off-white/50 dark:bg-gray-800/50 text-charcoal dark:text-gray-200 outline-none focus:ring-2 focus:ring-soft-green/20 focus:border-soft-green transition-all"
            >
              <option value="30_days">Last 30 Days</option>
              <option value="3_months">Last 3 Months</option>
              <option value="1_year">This Year</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-black text-muted-gray uppercase tracking-widest">Transaction Type</label>
            <div className="grid grid-cols-3 gap-2">
              {['all', 'deposit', 'withdrawal'].map(type => (
                <button
                  key={type}
                  onClick={() => setFilterType(type)}
                  className={`py-3 rounded-2xl text-xs font-black capitalize transition-all border ${
                    filterType === type 
                      ? 'bg-soft-green text-white border-soft-green' 
                      : 'bg-off-white dark:bg-gray-800 text-muted-gray border-light-gray/20 dark:border-gray-700/20'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          <Button onClick={() => setIsFilterModalOpen(false)} className="w-full py-4 mt-4 font-black">Apply Filters</Button>
        </div>
      </ResponsiveModal>
      
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-charcoal dark:text-white tracking-tight">Transaction History</h1>
          <p className="text-muted-gray mt-1 font-medium">Review and export your completed transactions.</p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button onClick={() => setIsExportModalOpen(true)} variant="secondary" className="gap-2 px-6 py-3.5 rounded-2xl font-black text-xs sm:text-sm shadow-sm hover:shadow-md transition-all">
            <HugeiconsIcon icon={Download01Icon} size={18} />
            Export Records
          </Button>
        </div>
      </div>

      <div className="bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md p-4 rounded-[2rem] border border-light-gray/20 dark:border-dark-border/20 flex items-center gap-4">
        <div 
          className="relative flex-1 cursor-pointer"
          onClick={() => setIsCmdkOpen(true)}
        >
          <HugeiconsIcon icon={Search01Icon} size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-gray" />
          <input 
            type="text" 
            readOnly
            placeholder="Search reference or client..." 
            className="w-full pl-10 pr-12 py-2.5 bg-off-white/50 dark:bg-gray-800/50 border border-light-gray/20 dark:border-gray-700/20 rounded-2xl text-sm focus:ring-soft-green focus:border-soft-green outline-none cursor-pointer"
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2 hidden sm:flex items-center gap-1 px-1.5 py-0.5 rounded border border-light-gray/30 dark:border-gray-700 bg-white dark:bg-gray-900 text-[10px] font-black text-muted-gray">
            <span>⌘</span><span>K</span>
          </div>
        </div>

        {/* Desktop Filters */}
        <div className="hidden lg:flex items-center gap-4">
          <div className="flex items-center gap-2">
            <HugeiconsIcon icon={Calendar01Icon} size={18} className="text-muted-gray" />
            <select 
              value={dateFilter}
              onChange={handleDateFilterChange}
              className="border border-light-gray/20 dark:border-gray-700/50 rounded-xl px-3 py-2 text-sm bg-white/50 dark:bg-dark-surface/50 text-charcoal dark:text-gray-200 outline-none focus:border-soft-green"
            >
              <option value="30_days">Last 30 Days</option>
              <option value="3_months">Last 3 Months</option>
              <option value="1_year">This Year</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <HugeiconsIcon icon={FilterIcon} size={18} className="text-muted-gray" />
            <select 
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="border border-light-gray/20 dark:border-gray-700/50 rounded-xl px-3 py-2 text-sm bg-white/50 dark:bg-dark-surface/50 text-charcoal dark:text-gray-200 outline-none focus:border-soft-green"
            >
              <option value="all">All Types</option>
              <option value="deposit">Deposits</option>
              <option value="withdrawal">Withdrawals</option>
            </select>
          </div>
        </div>

        {/* Mobile Filter Toggle */}
        <button 
          onClick={() => setIsFilterModalOpen(true)}
          className="lg:hidden p-2.5 bg-off-white dark:bg-gray-800 border border-light-gray/20 dark:border-gray-700/20 rounded-2xl text-muted-gray hover:text-soft-green transition-colors"
        >
          <HugeiconsIcon icon={FilterIcon} size={20} />
        </button>
      </div>

      <div className="bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md rounded-[2.5rem] border border-light-gray/20 dark:border-dark-border/20 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-off-white/30 dark:bg-gray-800/30 text-muted-gray text-[10px] uppercase font-black tracking-[0.2em]">
              <tr>
                <th className="px-8 py-5">Date & Time</th>
                <th className="px-8 py-5">Order ID</th>
                <th className="px-8 py-5">Type</th>
                <th className="px-8 py-5">Client</th>
                <th className="px-8 py-5 text-right">USD</th>
                <th className="px-8 py-5 text-right">NGN</th>
                <th className="px-8 py-5 text-right">Cashback</th>
                <th className="px-8 py-5 text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-light-gray/10 dark:divide-dark-border/10">
              {filteredHistory.map((item) => (
                <tr 
                  key={item.id} 
                  onClick={() => navigate(`/transaction/${item.id}`)}
                  className="hover:bg-soft-green/5 transition-colors group cursor-pointer"
                >
                  <td className="px-8 py-6 text-xs text-muted-gray font-mono">{new Date(item.created_at).toLocaleString()}</td>
                  <td className="px-8 py-6 font-bold text-charcoal dark:text-gray-200">{item.link_token || item.reference || item.id}</td>
                  <td className="px-8 py-6">
                    <span className={`text-[10px] px-3 py-1.5 rounded-full font-black uppercase tracking-wider ${
                      item.transaction_type === 'deposit' ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400' : 'bg-purple-500/10 text-purple-600 dark:text-purple-400'
                    }`}>
                      {item.transaction_type}
                    </span>
                  </td>
                  <td className="px-8 py-6">
                    <p className="font-bold text-charcoal dark:text-gray-300">{item.client?.name || item.client?.email || 'N/A'}</p>
                  </td>
                  <td className="px-8 py-6 font-black text-charcoal dark:text-white text-right">
                    ${parseFloat(item.amount_usd || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className="px-8 py-6 font-black text-soft-green text-right">
                    ₦{parseFloat(item.amount_sent || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className="px-8 py-6 text-right">
                    {item.cashback_earned > 0 ? (
                      <span className="text-[10px] font-black text-purple-500 bg-purple-500/10 px-2 py-1 rounded-lg">
                        ₦{item.cashback_earned.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    ) : (
                      <span className="text-[10px] font-bold text-muted-gray opacity-30">₦0.00</span>
                    )}
                  </td>
                  <td className="px-8 py-6 text-center">
                    <span className={`text-[10px] font-black uppercase tracking-wider px-3 py-1.5 rounded-full ${
                      ['completed', 'verified'].includes(item.status) ? 'bg-soft-green/10 text-soft-green' :
                      ['failed', 'rejected', 'cancelled'].includes(item.status) ? 'bg-red-500/10 text-red-500' :
                      'bg-yellow-500/10 text-yellow-500'
                    }`}>
                      {item.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredHistory.length === 0 && (
            <div className="p-12 text-center text-muted-gray text-sm">
                No transaction records found matching your filters.
            </div>
        )}
        
        {/* Pagination Controls */}
        {pagination.last_page > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-light-gray dark:border-dark-border bg-gray-50/50 dark:bg-gray-800/30">
            <span className="text-xs text-muted-gray font-medium">
              Page {pagination.current_page} of {pagination.last_page} ({pagination.total} records)
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 text-xs font-bold rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50 hover:bg-white dark:hover:bg-gray-700 transition-colors"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(p => Math.min(pagination.last_page, p + 1))}
                disabled={page === pagination.last_page}
                className="px-3 py-1.5 text-xs font-bold rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50 hover:bg-white dark:hover:bg-gray-700 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <ResponsiveModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        title="Export Transaction History"
      >
        <div className="py-6 space-y-4">
          <button onClick={handleExportPDF} className="w-full flex items-center justify-between p-6 bg-off-white/50 dark:bg-gray-800/50 rounded-3xl border border-light-gray/20 dark:border-gray-700/50 hover:bg-soft-green/5 transition-all group">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-red-500/10 text-red-500 rounded-2xl group-hover:scale-110 transition-transform"><HugeiconsIcon icon={File02Icon} size={24} /></div>
              <div className="text-left"><p className="font-black text-charcoal dark:text-white">PDF Document</p></div>
            </div>
            <HugeiconsIcon icon={Download01Icon} size={20} className="text-muted-gray" />
          </button>
          <button onClick={handleExportCSV} className="w-full flex items-center justify-between p-6 bg-off-white/50 dark:bg-gray-800/50 rounded-3xl border border-light-gray/20 dark:border-gray-700/50 hover:bg-soft-green/5 transition-all group">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-500/10 text-blue-500 rounded-2xl group-hover:scale-110 transition-transform"><HugeiconsIcon icon={DatabaseIcon} size={24} /></div>
              <div className="text-left"><p className="font-black text-charcoal dark:text-white">CSV Spreadsheet</p></div>
            </div>
            <HugeiconsIcon icon={Download01Icon} size={20} className="text-muted-gray" />
          </button>
          <div className="pt-4"><Button variant="primary" onClick={() => setIsExportModalOpen(false)} className="w-full py-4 font-black bg-soft-green text-white">Cancel</Button></div>
        </div>
      </ResponsiveModal>
    </div>
  );
};

export default AgentHistory;